const AUTH_APP_URL = import.meta.env.VITE_AUTH_APP_URL || 'http://localhost:3000';
const RECEPTION_APP_URL = import.meta.env.VITE_RECEPTION_APP_URL || 'http://localhost:5001';
const POS_APP_URL = import.meta.env.VITE_POS_APP_URL || 'http://localhost:5002';
const ACCOUNTANT_APP_URL = import.meta.env.VITE_ACCOUNTANT_APP_URL || 'http://localhost:5003';
const INVENTORY_APP_URL = import.meta.env.VITE_INVENTORY_APP_URL || 'http://localhost:5004';
const GROUP_EVENTS_APP_URL = import.meta.env.VITE_GROUP_EVENTS_APP_URL || 'http://localhost:5006';
const HR_APP_URL = import.meta.env.VITE_HR_APP_URL || 'http://localhost:5008';

const SPA_APP_URL = import.meta.env.VITE_SPA_APP_URL || 'http://localhost:5010';
const MARKETING_APP_URL = import.meta.env.VITE_MARKETING_APP_URL || 'http://localhost:5005';
const TOUR_AGENTS_APP_URL = import.meta.env.VITE_TOUR_AGENTS_APP_URL || 'http://localhost:5011';
const AFRICA_VERIFY_APP_URL = import.meta.env.VITE_AFRICA_VERIFY_APP_URL || 'http://localhost:5012';
const ODO_CARS_APP_URL = import.meta.env.VITE_ODO_CARS_APP_URL || 'http://localhost:5013';
const COMMUNICATION_HUB_APP_URL =
  import.meta.env.VITE_COMMUNICATION_HUB_APP_URL || 'http://localhost:5015';
const REPORTS_APP_URL = import.meta.env.VITE_REPORTS_APP_URL || 'http://localhost:5014';

const APP_KEY_MAP: Record<string, string> = {
  [import.meta.env.VITE_APP_AUTH_KEY || 'localhost:3000']: 'auth',
  [import.meta.env.VITE_APP_RECEPTION_KEY || 'localhost:5001']: 'reception',
  [import.meta.env.VITE_APP_POS_KEY || 'localhost:5002']: 'pos',
  [import.meta.env.VITE_APP_ACCOUNTANT_KEY || 'localhost:5003']: 'accountant',
  [import.meta.env.VITE_APP_INVENTORY_KEY || 'localhost:5004']: 'inventory',
  [import.meta.env.VITE_APP_MARKETING_KEY || 'localhost:5005']: 'marketing',
  [import.meta.env.VITE_APP_SPA_KEY || 'localhost:5010']: 'spa',
  [import.meta.env.VITE_APP_GROUP_EVENTS_KEY || 'localhost:5006']: 'groupEvents',
  [import.meta.env.VITE_APP_HR_KEY || 'localhost:5008']: 'hr',
  [import.meta.env.VITE_APP_TOUR_AGENTS_KEY || 'localhost:5011']: 'tourAgents',
  [import.meta.env.VITE_APP_AFRICA_VERIFY_KEY || 'localhost:5012']: 'africaVerify',
  [import.meta.env.VITE_APP_ODO_CARS_KEY || 'localhost:5013']: 'odoCars',
  [import.meta.env.VITE_APP_COMMUNICATION_HUB_KEY || 'localhost:5015']: 'communicationHub',
  [import.meta.env.VITE_APP_REPORTS_KEY || 'localhost:5014']: 'reports',
};

export function getAppKeyFromUrl(url: string): string | null {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname;
    const port = urlObj.port;
    const key = port ? `${hostname}:${port}` : hostname;
    return APP_KEY_MAP[key] || null;
  } catch {
    return null;
  }
}

export const APP_ROUTES = {
  home: {
    name: 'Home',
    description: 'Dashboard & Overview',
    baseUrl: AUTH_APP_URL,
    icon: 'Home',
  },
  reception: {
    name: 'Reception',
    description: 'Room & Guest Management',
    baseUrl: RECEPTION_APP_URL,
    icon: 'Hotel',
  },
  pos: {
    name: 'Point of Sale',
    description: 'Order Taking & Payments',
    baseUrl: POS_APP_URL,
    icon: 'PointOfSale',
  },
  accountant: {
    name: 'Accountant',
    description: 'Transactions & Reconciliation',
    baseUrl: ACCOUNTANT_APP_URL,
    icon: 'Calculate',
  },
  inventory: {
    name: 'Inventory',
    description: 'Stock & Purchase Orders',
    baseUrl: INVENTORY_APP_URL,
    icon: 'Inventory',
  },
  spa: {
    name: 'Spa',
    description: 'Therapist & Treatment Management',
    baseUrl: SPA_APP_URL,
    icon: 'Spa',
  },
  marketing: {
    name: 'Guest Sales',
    description: 'Guest inquiries, journey & touchpoints',
    baseUrl: MARKETING_APP_URL,
    icon: 'Campaign',
  },
  groupEvents: {
    name: 'Group Events',
    description: 'Group Bookings, Catering & QR Check-In',
    baseUrl: GROUP_EVENTS_APP_URL,
    icon: 'Group',
  },
  hr: {
    name: 'Human Resources',
    description: 'Employee Performance & Scheduling',
    baseUrl: HR_APP_URL,
    icon: 'Person',
  },
  reports: {
    name: 'Reports',
    description: 'Business Intelligence & Analytics',
    baseUrl: REPORTS_APP_URL,
    icon: 'Assessment',
  },
  tourAgents: {
    name: 'Safari Engine',
    description: 'Tour Agent Bookings & Invoicing',
    baseUrl: TOUR_AGENTS_APP_URL,
    icon: 'TravelExplore',
  },
  africaVerify: {
    name: 'Africa Verify',
    description: 'Document Verification & Vehicle Finance',
    baseUrl: AFRICA_VERIFY_APP_URL,
    icon: 'Verified',
  },
  odoCars: {
    name: 'ODO Cars',
    description: 'Vehicle Inventory & Reservations',
    baseUrl: ODO_CARS_APP_URL,
    icon: 'DirectionsCar',
  },
  communicationHub: {
    name: 'Communication Hub',
    description: 'Email, SMS & WhatsApp',
    baseUrl: COMMUNICATION_HUB_APP_URL,
    icon: 'Communication',
  },
} as const;

export type AppKey = keyof typeof APP_ROUTES;

export interface AppRoute {
  id: AppKey;
  name: string;
  description: string;
  baseUrl: string;
  url: string;
  icon: string;
  disabled?: boolean;
}

/** Kept visible, but not openable, until these modules are ready for guests. */
export const UNAVAILABLE_APP_IDS = ['spa', 'marketing', 'groupEvents'] as const;

export function isAppUnavailable(id: string): boolean {
  return (UNAVAILABLE_APP_IDS as readonly string[]).includes(id);
}

export const getAllAppRoutes = (): AppRoute[] => {
  return (Object.keys(APP_ROUTES) as AppKey[]).map(key => {
    const route = APP_ROUTES[key];
    return {
      id: key,
      name: route.name,
      description: route.description,
      baseUrl: route.baseUrl,
      url: route.baseUrl,
      icon: route.icon,
      disabled: isAppUnavailable(key),
    };
  });
};
